import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Send, Lock, Sparkles, ChevronRight } from 'lucide-react';

export default function About() {
  return (
    <div className="max-w-2xl mx-auto py-10 px-4 space-y-12">

      {/* Header */}
      <div className="text-center space-y-3">
        <div className="w-12 h-12 bg-brand-accent rounded-xl flex items-center justify-center mx-auto mb-4">
          <span className="font-bold text-white text-2xl leading-none">Y</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--color-brand-text)' }}>
          About Yhuu
        </h1>
        <p className="text-zinc-500 text-base leading-relaxed max-w-md mx-auto">
          The anonymous Q&A app built for real conversations.
        </p>
      </div>

      {/* Mission */}
      <div className="p-6 rounded-2xl border border-brand-border" style={{ background: 'var(--color-brand-card)' }}>
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-brand-text)' }}>Our Mission</h2>
        <p className="text-zinc-500 leading-relaxed text-sm">
          Yhuu was built on a simple belief: people deserve honest answers. Whether you're in a new relationship, questioning a friendship, or trying to understand someone's true feelings, getting the truth is rarely easy — especially when asking directly puts your vulnerability on the line.
        </p>
        <p className="text-zinc-500 leading-relaxed text-sm mt-3">
          Yhuu removes the friction. We give you a private, secure space to ask the questions you've been holding back, sent anonymously to the person you want answers from. No awkwardness. No exposure. Just clarity.
        </p>
      </div>

      {/* How it works */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-brand-text)' }}>How It Works</h2>

        {[
          {
            icon: <Sparkles className="w-5 h-5 text-brand-accent" />,
            step: '01',
            title: 'Ask Your Questions',
            desc: 'Write the questions you want answered honestly. You can also include what you expected their answer to be — for later comparison.',
          },
          {
            icon: <Send className="w-5 h-5 text-zinc-400" />,
            step: '02',
            title: 'Secret Send',
            desc: 'Yhuu delivers your questions to your person via WhatsApp, SMS, Snapchat, or Telegram — without ever revealing who sent them.',
          },
          {
            icon: <ShieldCheck className="w-5 h-5 text-emerald-500/70" />,
            step: '03',
            title: 'Vibe Check',
            desc: 'Their answers show up in your space. You can compare what you expected vs. what they actually said.',
          },
          {
            icon: <Lock className="w-5 h-5 text-brand-accent" />,
            step: '04',
            title: 'Stay Private',
            desc: 'Your identity is never disclosed. Every session is fully private. Only you can see the answers.',
          },
        ].map(item => (
          <div
            key={item.step}
            className="flex gap-4 p-5 rounded-2xl border border-brand-border"
            style={{ background: 'var(--color-brand-card)' }}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-xl border border-brand-border flex items-center justify-center"
              style={{ background: 'var(--color-brand-bg)' }}>
              {item.icon}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-brand-accent opacity-60">{item.step}</span>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--color-brand-text)' }}>{item.title}</h3>
              </div>
              <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Who it's for */}
      <div className="p-6 rounded-2xl border border-brand-border" style={{ background: 'var(--color-brand-card)' }}>
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-brand-text)' }}>Who Yhuu Is For</h2>
        <ul className="space-y-2 text-sm text-zinc-500">
          {[
            'People in relationships who want honest communication without confrontation.',
            "Anyone who suspects they're not getting the full truth.",
            "Those who want to understand someone's feelings before making a major decision.",
            'Users who value privacy and discretion above all else.',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-brand-accent flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="text-center pt-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-accent text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all group"
        >
          Get Started
          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <p className="text-xs text-zinc-600 mt-4">
          Questions? Reach us at{' '}
          <a href="mailto:new@yhuu.life" className="text-brand-accent hover:underline">new@yhuu.life</a>
        </p>
      </div>
    </div>
  );
}
